import Vue from 'vue';

Vue.component('new-input-component', {
    props: {
        inputId: {
            type: String,
        },
        labelId: {
            type: String,
        },
        errors: {
            type: Object,
        },
        isTextArea: {
            type: Boolean,
            default: false,
        },
        isBrandstoreSearchInput: {
            type: Boolean,
            default: false,
        },
        dropdownValue: {
            type: String,
            required: false,
            default: '',
        },
        customInputValue: {
            type: String | Number,
            required: false,
            default: '',
        },
        handleDropdownToggle: {
            type: Function,
            required: false,
        },
        showErrorForId: {
            type: String,
            required: false,
        },
        showErrorForValue: {
            type: String,
            required: false,
        },
        customErrorId: {
            type: String,
            required: false,
            default: null,
        },
        customErrorCheck: {
            type: String,
            required: false,
            default: null,
        },
        currentSite: {
            type: String,
            required: false,
            default: null,
        },
        errorContainerId: {
            type: String,
            required: false,
            default: null,
        },
    },

    data() {
        return {
            input: null,
            inputContainer: null,
            inputValue: this.dropdownValue || this.customInputValue || '',
            timer: null,
            isErrorVisible: false,
        };
    },

    mounted() {
        this.$nextTick(() => {
            const isDropdownValueSet =
                this.dropdownValue && this.dropdownValue.length > 0;

            // Safe reference to input element using $refs first, fallback to querySelector
            this.input =
                this.$refs.input || this.$el.querySelector(`#${this.inputId}`);

            if (!this.input) {
                console.warn(`Input element with ID ${this.inputId} not found`);
                return;
            }

            this.inputContainer = this.$el.querySelector(
                `#container-${this.inputId}`,
            );

            this.handleInputChange();

            if (isDropdownValueSet || Boolean(this.customInputValue)) {
                this.handleInputLabelMoved();
            }

            // Add event listeners only if input exists
            this.input.addEventListener(
                'focus',
                this.handleAddPhonePlaceholder,
            );
            this.input.addEventListener(
                'blur',
                this.handleRemovePhonePlaceholder,
            );
        });

        EventBus.$on('brandstore-dropdown-value-selected', (value) => {
            if (this.inputId === value.inputId) {
                let parsedStore = JSON.parse(value.store);
                this.inputValue = parsedStore.title;
            }
        });

        EventBus.$on('brandstore-search-reset', (inputId) => {
            if (this.inputId === inputId) {
                this.inputValue = '';
            }
        });
    },

    unmounted() {
        clearTimeout(this.timer);

        // Only remove event listeners if input exists
        if (this.input) {
            this.input.removeEventListener(
                'focus',
                this.handleAddPhonePlaceholder,
            );
            this.input.removeEventListener(
                'blur',
                this.handleRemovePhonePlaceholder,
            );
        }
    },

    watch: {
        customInputValue(value) {
            this.inputValue = value;
            if (Boolean(this.customInputValue)) {
                this.handleInputLabelMoved();
            }
        },

        dropdownValue() {
            this.handleInputLabelMoved();
        },

        inputValue(value) {
            if (this.isBrandstoreSearchInput) {
                this.$parent.store = value;
            }
            this.handleInputLabelMoved();
        },

        isErrorVisible(newValue, oldValue) {
            // Announce error state changes to screen readers
            if (newValue !== oldValue && this.input) {
                // Update aria-invalid attribute
                this.input.setAttribute(
                    'aria-invalid',
                    newValue ? 'true' : 'false',
                );

                // Update aria-describedby
                if (newValue && this.errorContainerId) {
                    this.input.setAttribute(
                        'aria-describedby',
                        this.errorContainerId,
                    );
                } else {
                    this.input.removeAttribute('aria-describedby');
                }
            }
        },

        errors: {
            handler: function (newErrors) {
                const errs = newErrors.errors;

                // 1. Custom error takes priority
                if (this.customErrorId && this.customErrorCheck) {
                    const customList = errs[this.customErrorId] || [];
                    this.isErrorVisible = customList.some((e) =>
                        e.includes(this.customErrorCheck),
                    );
                    return;
                }

                // 2. Brandstore‐search special case
                if (
                    (errs.dealerCode || errs.store) &&
                    this.inputId === 'searchBrandstore'
                ) {
                    this.isErrorVisible = true;
                    return;
                }

                // 3. General case: no errors for this input (or override id) => hide
                const overrideId = this.showErrorForId || this.inputId;
                if (!errs[this.inputId] && !errs[overrideId]) {
                    this.isErrorVisible = false;
                    return;
                }

                // 4. Otherwise show error
                this.isErrorVisible = true;
            },
            deep: true,
        },
    },

    computed: {
        handleCheckDropdownSelected() {
            if (this.dropdownValue && this.dropdownValue.length > 0) {
                this.isErrorVisible = false;
                return this.handleHiddenError();
            }
        },

        handleShowContainerError() {
            return this.isErrorVisible ? 'new-input__container--error' : '';
        },

        handleShowEmailFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('email', 'valid email address');
        },

        handleShowEmailEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('email', 'blank');
        },

        handleShowFirstNameEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('firstName', 'blank');
        },

        handleShowLastNameEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('lastName', 'blank');
        },

        handleShowZipCodeEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('postalCode', 'blank');
        },

        handleShowPostalCodeMax10Error() {
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('postalCode', 'POSTAL_MAX_10');
        },

        handleShowPostalCodeMax15Error() {
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('postalCode', 'POSTAL_MAX_15');
        },

        handleShowDealerEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();

            if (Boolean(this.$parent.store)) return this.handleHiddenError();

            if (!this.inputId.includes('store'))
                return this.handleHiddenError();

            const hasError = this.errors.errors.hasOwnProperty('store');

            if (!hasError) return this.handleHiddenError();

            const isError = this.errors.errors['store'][0].includes('blank');

            if (!isError) return this.handleHiddenError();
        },

        handleShowPhoneNumberEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('phone', 'blank');
        },

        handleShowPhoneNumberFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();

            const errors = [
                this.handleErrorChecks('phone', 'phone number format'),
                this.handleErrorChecks('phone', 'must be an integer'),
                this.handleErrorChecks(
                    'phone',
                    'does not seem to be a valid phone number',
                ),
            ];

            if (errors.includes(undefined)) return;

            return this.handleHiddenError();
        },

        handleShowLicensePlateFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('licensePlate', 'is invalid');
        },

        handleShowHouseNumberEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('houseNumber', 'blank');
        },

        handleShowHouseNumberFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('houseNumber', 'must be an integer');
        },

        handleHeardFromEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('heardFrom', 'blank');
        },

        handleSiftingQuestionEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('siftingQuestion', 'blank');
        },

        handleInvoiceNumberEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('invoiceNumber', 'blank');
        },

        handleMaintenanceDateEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('maintenanceDate', 'blank');
        },

        handleShowCityNameEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('city', 'blank');
        },

        handleShowCompanyEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('company', 'blank');
        },

        handleShowStreetNameEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('streetName', 'blank');
        },

        handleShowMessageEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('message', 'blank');
        },

        handleShowActivityLocationEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('activityLocation', 'blank');
        },

        handleShowEmptyStoreError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('store', 'blank');
        },

        handleFinancingTypeEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('financingType', 'blank');
        },

        handleShowEmptyBrandstoreError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            if (this.showErrorForId && this.showErrorForValue) {
                return this.handleErrorCustomInputChecks(
                    this.showErrorForValue,
                    this.showErrorForId,
                );
            }
            return this.handleErrorChecks('brandStore', 'blank');
        },

        handleShowCountryEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('country', 'blank');
        },

        handleShowEmptyTopicError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('subject', 'blank');
        },

        handleShowCvFileSizeTooLargeError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('cv', 'File size too large');
        },

        handleShowCvFileSizeTooSmallError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('cv', 'File size too small');
        },

        handleShowFileExtensionError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('cv', 'Extension not allowed');
        },

        handleShowEmailConsentRequiredError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('emailConsent', 'blank');
        },

        handleShowSmsConsentRequiredError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('smsConsent', 'blank');
        },

        handleShowPhoneConsentRequiredError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('phoneConsent', 'blank');
        },

        handleShowStoreCountryRequiredError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('storeCountry', 'blank');
        },

        handleShowSirenSiretWrongFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('sirenSiret', 'invalid format');
        },

        handleShowVinEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('vin', 'blank');
        },

        handleShowOwnerManualError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('vin', 'owner-manual-error');
        },

        handleShowVinError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('vin', '17 characters');
        },

        handleShowFleetSizeError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('fleetSize', 'blank');
        },

        handleShowCustomError() {
            return this.handleErrorCustomInputChecks(
                this.customErrorCheck,
                this.customErrorId,
            );
        },

        handleShowHouseNumberTooLongError() {
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('houseNumber', 'HOUSE_NUMBER_MAX_9');
        },

        handleShowFirstNameMax50Error() {
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('firstName', 'FIRST_NAME_MAX_50');
        },

        handleShowLastNameMax50Error() {
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('lastName', 'LAST_NAME_MAX_50');
        },

        handleShowGenderEmptyError() {
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('gender', 'blank');
        },

        handleShowPreferredTimeEmptyError() {
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('preferredTime', 'blank');
        },
    },

    methods: {
        handleAddPhonePlaceholder() {
            if (
                this.currentSite !== 'mgMotorsHungary' ||
                this.inputId !== 'phone' ||
                !this.input
            ) {
                return;
            }
            this.input.placeholder = '+36XXXXXXXXXXX';
        },

        handleRemovePhonePlaceholder() {
            if (
                this.currentSite !== 'mgMotorsHungary' ||
                this.inputId !== 'phone' ||
                !this.input
            ) {
                return;
            }
            this.input.placeholder = '';
        },

        handleShowDownPaymentError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return false;
            return (
                !this.$parent.isFormVisible &&
                this.errors.errors.hasOwnProperty('downPayment')
            );
        },

        handleHiddenError() {
            return 'new-input__error--hidden';
        },

        handleErrorCustomInputChecks(errorMessage, customErrorKey = null) {
            // If custom error props are not set, hide this
            if (!this.customErrorId || !this.customErrorCheck)
                return this.handleHiddenError();

            const inputId = customErrorKey || this.inputId;

            const hasError = this.errors.errors.hasOwnProperty(inputId);

            if (!hasError) return this.handleHiddenError();

            const isError = this.errors.errors[inputId].find((error) =>
                error.includes(errorMessage),
            );

            if (!isError) return this.handleHiddenError();

            // Return empty string if error should be shown (removes the hidden class)
            return '';
        },

        handleErrorChecks(id, errorMessage, customErrorKey = null) {
            // This check is now redundant here because individual computed props handle the customErrorId check
            // but kept for potential direct calls if any exist. Best practice would be to rely on computed props.
            if (this.customErrorId) return this.handleHiddenError();

            if (!this.inputId.includes(id)) return this.handleHiddenError();

            const errorKeyToCheck = customErrorKey || this.inputId;
            const hasError = this.errors.errors.hasOwnProperty(errorKeyToCheck);

            if (!hasError) return this.handleHiddenError();

            const isError = this.errors.errors[errorKeyToCheck].find((error) =>
                error.includes(errorMessage),
            );

            if (!isError) return this.handleHiddenError();

            // Return empty string if error should be shown (removes the hidden class)
            return '';
        },

        // Check for non numeric pasted user input and sanitize it to only leave numbers
        handleNumericPaste(event) {
            event.preventDefault();

            const digits = event.clipboardData
                .getData('text')
                .replace(/\D/g, '');

            const { selectionStart: start, selectionEnd: end } = event.target;
            this.inputValue =
                (this.inputValue || '').slice(0, start) +
                digits +
                (this.inputValue || '').slice(end);
        },

        // Sanitize pasted input to only digits and spaces (e.g. SIREN/SIRET "840 712 210")
        handleDigitsAndSpacesPaste(event) {
            event.preventDefault();

            const digitsAndSpaces = event.clipboardData
                .getData('text')
                .replace(/[^\d ]/g, '');

            const { selectionStart: start, selectionEnd: end } = event.target;
            this.inputValue =
                (this.inputValue || '').slice(0, start) +
                digitsAndSpaces +
                (this.inputValue || '').slice(end);
        },

        handleInputChange() {
            if (this.handleDropdownToggle) return;
            if (!this.input) return; // Defensive check

            this.timer = setTimeout(() => {
                this.inputValue = this.input.value;
            }, 100);
        },

        handleInputLabelMoved() {
            const label = this.$el.querySelector(`#${this.labelId}`);

            if (!label) return;

            const isLabelBlack = label.classList.contains(
                'new-input__label-black',
            );
            if (isLabelBlack) return;

            const isInputEmpty = this.inputValue?.length === 0;

            const isDropdownEmpty = this.dropdownValue?.length === 0;

            if (isInputEmpty && isDropdownEmpty) {
                this.handleRemoveLabelClass(label);
                return;
            }

            this.handleAddLabelClass(label);
        },

        handleAddLabelClass(label) {
            if (this.isTextArea) {
                label.classList.add('new-input__textarea-label--moved');
                return;
            }

            label.classList.add('new-input__label--moved');
        },

        handleRemoveLabelClass(label) {
            if (this.isTextArea) {
                label.classList.remove('new-input__textarea-label--moved');
                return;
            }

            label.classList.remove('new-input__label--moved');
        },

        toggleDropdown(event) {
            if (
                this.handleDropdownToggle &&
                typeof this.handleDropdownToggle === 'function'
            ) {
                this.handleDropdownToggle(event);
            }
        },
    },
});
